/**
 * «Посеять без плана» — мастер из четырёх вопросов (ситуация В-2).
 *
 * Где сеем → что сеем → сколько и когда → уже посеяли? Первый вопрос именно
 * про контур: от него считается площадь, а площадь — базовая цифра для всего
 * остального (нормы, семена, операции).
 *
 * Ключевой момент — последний шаг. Пока выбран «запланирован», в системе
 * только план. Как только выбран «посеян» — форма показывает превью
 * тех.операции «Сев» и прямо говорит, что её создаст: густота, норма высева и
 * глубина это параметры ОПЕРАЦИИ, а не свойства посева (решение founder 07.08).
 *
 * Свой контур под посев (нарисовать) появится вместе с API посевного контура
 * ADR-061 — здесь пока «поле целиком» и готовые зоны поля.
 */
import { useEffect, useMemo, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { cn } from '@/lib/utils';
import { ApiError } from '@/lib/api';
import { toast } from '@/lib/toast';
import { useFieldStates } from '@/lib/reference/fieldCatalogs';
import { useCompanyOpTypes } from '@/components/operations/operationsApi';
import { fieldStateOptions } from '../constants';
import { cropPickerKey, useCropPicker, useSubzones, type CropPickerItem } from '../api';
import { useSowWithoutPlan } from './api';
import type { FieldSeasonPlan } from '../plan/api';
import { draftFromPlan, planDeviations, shiftsProgram } from '../plan/planPrefill';
import {
  SEEDING_RATE_UNITS,
  SOW_STEPS,
  areaDelta,
  freeAreaHa,
  initialDraft,
  guessSowOpType,
  outcomeSummary,
  rateUnitHint,
  seedNeedKg,
  stepBlocker,
  toSowBody,
  type SeedingRateUnit,
  type SowDraft,
  type SowStep,
} from './sowMath';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  fieldId: string;
  fieldName: string;
  fieldAreaHa: number | null;
  /** Уже занято под посевы в этом сезоне, га — для «свободно N га». */
  occupiedHa: number | null;
  seasonId: string;
  isRiceSystem?: boolean;
  /**
   * Предзаполнение участка. Компакт-вкладка «Посевы» открывает мастер прямо из
   * строки зоны («Посеять» / «Посеять по плану») — участок там уже известен, и
   * шаг «Где сеем» должен пропускаться, а не спрашиваться заново.
   */
  initialSubzoneId?: string | null;
  /** С какого шага открыть. Для входа из строки — 'what'. */
  initialStep?: SowStep;
  /**
   * План сезона на это поле (мост «бюджет → поле»).
   *
   * Передан — мастер открывается ЗАПОЛНЕННЫМ из бюджета («Посеять по плану»,
   * экран 4 прототипа) и показывает расхождения с утверждённой программой по
   * ходу заполнения. Не передан — обычный сев без плана, как и был.
   */
  plan?: FieldSeasonPlan | null;
  /**
   * «Посеять иначе»: план есть, но агроном сознательно сеет своё. Форма
   * стартует пустой, а расхождения с бюджетом всё равно показываются — иначе
   * отклонение обнаружится только в отчёте, через полгода.
   */
  planMode?: 'by_plan' | 'otherwise';
}

const STEP_TITLES: Record<SowStep, { title: string; hint: string }> = {
  where: { title: 'Где сеем', hint: 'контур и площадь' },
  what: { title: 'Что сеем', hint: 'культура и гибрид' },
  how: { title: 'Сколько и когда', hint: 'густота, норма, дата' },
  status: { title: 'Уже посеяли?', hint: 'статус на входе' },
};

function num(v: string): number | null {
  if (v.trim() === '') return null;
  const n = Number(v.replace(',', '.'));
  return Number.isFinite(n) ? n : null;
}

function errText(e: unknown): string {
  if (e instanceof ApiError) {
    const d = (e as ApiError & { detail?: unknown }).detail;
    if (d && typeof d === 'object' && 'message' in d) return String((d as { message: unknown }).message);
    return e.message;
  }
  return e instanceof Error ? e.message : 'Не удалось сохранить посев.';
}

export function SowWizard({
  open,
  onOpenChange,
  fieldId,
  fieldName,
  fieldAreaHa,
  occupiedHa,
  seasonId,
  isRiceSystem = false,
  initialSubzoneId = null,
  initialStep = 'where',
  plan = null,
  planMode = 'by_plan',
}: Props) {
  const usePlanDraft = Boolean(plan?.has_plan) && planMode === 'by_plan';
  const [step, setStep] = useState<SowStep>(initialStep);
  const [draft, setDraft] = useState<SowDraft>(() =>
    usePlanDraft && plan
      ? draftFromPlan(plan, fieldAreaHa)
      : initialDraft(fieldAreaHa, { subzoneId: initialSubzoneId }),
  );
  const [search, setSearch] = useState('');
  const [error, setError] = useState<string | null>(null);

  const picker = useCropPicker();
  const zones = useSubzones(fieldId);
  const opTypes = useCompanyOpTypes();
  const fieldStates = useFieldStates();
  const sow = useSowWithoutPlan(fieldId);

  const free = freeAreaHa(fieldAreaHa, occupiedHa);
  // Участок уже занят в этом сезоне — второй посев законен (пожнивной), но
  // требует явной отметки: иначе повторный сабмит молча заведёт дубль.
  const leafBusy = (occupiedHa ?? 0) > 0 && draft.subzoneId == null;

  // Открытие мастера = чистый черновик с подставленной площадью контура.
  // Без этого «открыл → Дальше» уносит площадь null до самой факт-операции.
  useEffect(() => {
    if (!open) return;
    const zone = (zones.data ?? []).find((z) => z.id === initialSubzoneId);
    setDraft(
      usePlanDraft && plan
        ? draftFromPlan(plan, fieldAreaHa)
        : initialDraft(fieldAreaHa, {
            subzoneId: initialSubzoneId,
            subzoneAreaHa: zone?.area_ha ?? null,
          }),
    );
    setStep(initialStep);
    setError(null);
    // zones.data намеренно вне зависимостей: подгрузка зон не должна затирать
    // уже введённый черновик посреди заполнения.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, initialSubzoneId, initialStep, fieldAreaHa, usePlanDraft]);

  const cropOptions: CropPickerItem[] = useMemo(() => {
    const items = [...(picker.data ?? [])].sort((a, b) => a.name.localeCompare(b.name, 'ru'));
    const q = search.trim().toLowerCase();
    return q ? items.filter((c) => c.name.toLowerCase().includes(q)) : items;
  }, [picker.data, search]);

  const fallowOptions = useMemo(
    () => fieldStateOptions(fieldStates.data ?? [], isRiceSystem),
    [fieldStates.data, isRiceSystem],
  );

  // Тип операции «Сев» подставляем сам — искать строку в длинном списке
  // хозяйства агроном не должен. Не нашли — выберет руками, не подставляем
  // случайное.
  const guessedOpType = useMemo(() => guessSowOpType(opTypes.data ?? []), [opTypes.data]);
  const effectiveOpTypeId = draft.sowOperationTypeId ?? guessedOpType?.id ?? null;

  const blocker = stepBlocker(step, { ...draft, sowOperationTypeId: effectiveOpTypeId });
  // Расхождения с утверждённой программой видны ПО ХОДУ заполнения, а не в
  // отчёте через полгода. Ничего не блокируют — бюджет с поля не правится, но
  // и молчать о том, что сев идёт не по плану, нельзя.
  const hasPlan = Boolean(plan?.has_plan);
  const deviations = useMemo(
    () => (plan && hasPlan ? planDeviations(plan, draft) : []),
    [plan, hasPlan, draft],
  );
  const programShift = shiftsProgram(deviations);
  const stepIdx = SOW_STEPS.indexOf(step);
  const delta = areaDelta(draft);
  const seeds = seedNeedKg(draft);

  function patch(next: Partial<SowDraft>) {
    setDraft((d) => ({ ...d, ...next }));
  }

  function pickZone(id: string | null) {
    const zone = (zones.data ?? []).find((z) => z.id === id);
    const contour = id == null ? fieldAreaHa : (zone?.area_ha ?? null);
    // Площадь по контуру подставляется в «засеяно фактически» только если
    // агроном ещё не правил её руками — иначе форма затирала бы ввод.
    patch({
      subzoneId: id,
      contourAreaHa: contour,
      areaHa: draft.areaHa == null ? contour : draft.areaHa,
    });
  }

  /**
   * @param andMore — «пол-поля одним, пол-поля другим»: не закрываем диалог,
   * а возвращаемся на шаг 1 с чистым черновиком. Занятая площадь пересчитается
   * сама — `occupiedHa` придёт обновлённым после инвалидации.
   */
  function submit(andMore = false) {
    setError(null);
    const body = toSowBody({ ...draft, sowOperationTypeId: effectiveOpTypeId }, seasonId);
    sow.mutate(body, {
      onSuccess: (res) => {
        toast.success(res.summary);
        setDraft(initialDraft(fieldAreaHa));
        setStep('where');
        if (!andMore) onOpenChange(false);
      },
      onError: (e) => setError(errText(e)),
    });
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="flex max-h-[85dvh] max-w-3xl flex-col">
        <DialogHeader>
          <DialogTitle>
            {hasPlan
              ? `${planMode === 'by_plan' ? 'Посеять по плану' : 'Посеять иначе'} · ${fieldName}`
              : `Добавить посев · ${fieldName}`}
          </DialogTitle>
          <DialogDescription>
            {hasPlan
              ? planMode === 'by_plan'
                ? `План из бюджета ${plan?.season_year ?? ''} подставлен — проверьте и допишите то, чего бюджет знать не мог: гибрид, точную дату, фактическую площадь.`
                : `По плану здесь ${plan?.crop_name ?? 'другая культура'} — бюджет не переписывается, расхождение будет видно как есть.`
              : `Плана на это поле нет — заводим посев вручную. Свободно ${free ?? '—'} га из ${fieldAreaHa ?? '—'} га.`}
          </DialogDescription>
        </DialogHeader>

        {deviations.length > 0 && (
          <Alert>
            <AlertDescription className="space-y-2">
              <div className="font-medium">
                {programShift
                  ? 'Это расходится с утверждённым бюджетом'
                  : 'Небольшое расхождение с планом'}
              </div>
              <ul className="space-y-1 text-xs">
                {deviations.map((d) => (
                  <li key={`${d.field}-${d.label}`}>
                    {d.label}: план {d.planned} → {d.actual} ({d.note})
                  </li>
                ))}
              </ul>
              {programShift && (
                <p className="text-xs">
                  Бюджет остаётся как есть — плановый посев не удаляется и не
                  пересчитывается. Причину отклонения система пока не спрашивает: её
                  хранение — следующий шаг, он требует решения по составу причин.
                </p>
              )}
            </AlertDescription>
          </Alert>
        )}

        <div className="flex flex-wrap gap-2">
          {SOW_STEPS.map((s, i) => (
            <div
              key={s}
              className={cn(
                'flex-1 min-w-[130px] rounded-md border px-3 py-2',
                s === step
                  ? 'border-primary bg-primary/10'
                  : i < stepIdx
                    ? 'border-border bg-muted'
                    : 'border-dashed border-border',
              )}
            >
              <span className="block text-xs font-semibold">
                {i + 1} · {STEP_TITLES[s].title}
              </span>
              <span className="block text-xs text-muted-foreground">
                {STEP_TITLES[s].hint}
              </span>
            </div>
          ))}
        </div>

        <div className="min-h-0 flex-1 space-y-4 overflow-y-auto pr-1">
          {step === 'where' && (
            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-wide text-muted-foreground">
                Выбор контура
              </Label>
              <button
                type="button"
                onClick={() => pickZone(null)}
                className={cn(
                  'flex w-full items-center justify-between rounded-lg border p-3 text-left',
                  draft.subzoneId == null ? 'border-primary bg-primary/10' : 'border-border',
                )}
              >
                <span>
                  <span className="block text-sm font-medium">Всё поле — эталонный контур</span>
                  <span className="block text-xs text-muted-foreground">
                    граница поля из земельного банка
                  </span>
                </span>
                <Badge variant="secondary">{fieldAreaHa ?? '—'} га</Badge>
              </button>
              {(zones.data ?? []).map((z) => (
                <button
                  key={z.id}
                  type="button"
                  onClick={() => pickZone(z.id)}
                  className={cn(
                    'flex w-full items-center justify-between rounded-lg border p-3 text-left',
                    draft.subzoneId === z.id ? 'border-primary bg-primary/10' : 'border-border',
                  )}
                >
                  <span>
                    <span className="block text-sm font-medium">{z.name ?? 'Зона поля'}</span>
                    <span className="block text-xs text-muted-foreground">готовая зона поля</span>
                  </span>
                  <Badge variant="secondary">{z.area_ha ?? '—'} га</Badge>
                </button>
              ))}
              <p className="text-xs text-muted-foreground">
                Свой контур под этот посев (нарисовать на карте) появится вместе с посевным
                контуром — пока сеем по полю целиком или по готовой зоне. Сумма посевов не может
                быть больше поля: правило держит и сервер.
              </p>
            </div>
          )}

          {step === 'what' && (
            <div className="space-y-3">
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Поиск культуры по названию…"
              />
              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-wide text-muted-foreground">
                  Культура
                </Label>
                <Select
                  value={draft.cropKey}
                  onValueChange={(v) => {
                    const item = (picker.data ?? []).find((c) => cropPickerKey(c) === v);
                    patch({ cropKey: v, cropName: item?.name, fallowKind: null });
                  }}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Выберите культуру хозяйства…" />
                  </SelectTrigger>
                  <SelectContent>
                    {cropOptions.map((c) => (
                      <SelectItem key={cropPickerKey(c)} value={cropPickerKey(c)}>
                        {c.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground">
                  Нужной культуры нет — заведите её в справочнике хозяйства. Дублей культур мы не
                  плодим.
                </p>
              </div>
              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-wide text-muted-foreground">
                  Или оставить под пар — занятие поля без культуры
                </Label>
                <Select
                  value={draft.fallowKind ?? ''}
                  onValueChange={(v) => patch({ fallowKind: v, cropKey: '', cropName: undefined })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="не выбрано" />
                  </SelectTrigger>
                  <SelectContent>
                    {fallowOptions.map((o) => (
                      <SelectItem key={o.code} value={o.code}>
                        {o.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          )}

          {step === 'how' && (
            <div className="space-y-4">
              <div className="grid gap-3 md:grid-cols-3">
                <div className="space-y-2">
                  <Label className="text-xs uppercase tracking-wide text-muted-foreground">
                    По контуру
                  </Label>
                  <div className="flex h-9 items-center rounded-md border border-border bg-muted px-3 text-sm tabular-nums">
                    {draft.contourAreaHa ?? '—'} га
                  </div>
                </div>
                <div className="space-y-2">
                  <Label className="text-xs uppercase tracking-wide text-muted-foreground">
                    Засеяно фактически
                  </Label>
                  <Input
                    inputMode="decimal"
                    value={draft.areaHa ?? ''}
                    onChange={(e) => patch({ areaHa: num(e.target.value) })}
                    placeholder="га"
                  />
                  {delta != null && delta !== 0 && (
                    <p className="text-xs text-muted-foreground">
                      расхождение {delta > 0 ? '+' : ''}
                      {delta} га — сохранится как есть
                    </p>
                  )}
                </div>
                <div className="space-y-2">
                  <Label className="text-xs uppercase tracking-wide text-muted-foreground">
                    Дата сева
                  </Label>
                  <Input
                    type="date"
                    value={draft.sowingDate ?? ''}
                    onChange={(e) => patch({ sowingDate: e.target.value || null })}
                  />
                  <p className="text-xs text-muted-foreground">
                    для «запланирован» — ориентировочная, для «посеян» — фактическая
                  </p>
                </div>
              </div>

              {delta != null && delta !== 0 && (
                <div className="space-y-2">
                  <Label className="text-xs uppercase tracking-wide text-muted-foreground">
                    Почему меньше контура · необязательно
                  </Label>
                  <Input
                    value={draft.areaDiffReason}
                    onChange={(e) => patch({ areaDiffReason: e.target.value })}
                    placeholder="напр.: не сеяли подтопленный угол"
                  />
                </div>
              )}

              {!draft.fallowKind && (
                <>
                  <div className="grid gap-3 md:grid-cols-4">
                    <div className="space-y-2 md:col-span-2">
                      <Label className="text-xs uppercase tracking-wide text-muted-foreground">
                        Густота сева
                      </Label>
                      <div className="flex gap-2">
                        <Input
                          inputMode="decimal"
                          value={draft.seedingRate ?? ''}
                          onChange={(e) => patch({ seedingRate: num(e.target.value) })}
                          placeholder="густота"
                        />
                        <Select
                          value={draft.seedingRateUnit}
                          onValueChange={(v) => patch({ seedingRateUnit: v as SeedingRateUnit })}
                        >
                          <SelectTrigger className="w-36">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {SEEDING_RATE_UNITS.map((u) => (
                              <SelectItem key={u.value} value={u.value}>
                                {u.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <p className="text-xs text-muted-foreground">
                        {rateUnitHint(draft.seedingRateUnit)}
                      </p>
                    </div>
                    <div className="space-y-2">
                      <Label className="text-xs uppercase tracking-wide text-muted-foreground">
                        Норма высева
                      </Label>
                      <Input
                        inputMode="decimal"
                        value={draft.seedingNormKgHa ?? ''}
                        onChange={(e) => patch({ seedingNormKgHa: num(e.target.value) })}
                        placeholder="кг/га"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-xs uppercase tracking-wide text-muted-foreground">
                        Глубина заделки
                      </Label>
                      <Input
                        inputMode="decimal"
                        value={draft.seedingDepthCm ?? ''}
                        onChange={(e) => patch({ seedingDepthCm: num(e.target.value) })}
                        placeholder="см"
                      />
                    </div>
                  </div>
                  <Alert>
                    <AlertDescription className="text-xs">
                      Густота, норма высева и глубина — параметры тех.операции «Сев», а не свойства
                      посева. Здесь они спрашиваются потому, что агроном заполняет всё одним
                      заходом; если посев заводится как «запланирован» — операции ещё нет.
                    </AlertDescription>
                  </Alert>
                </>
              )}
            </div>
          )}

          {step === 'status' && (
            <div className="space-y-3">
              {!draft.fallowKind && (
                <div className="grid gap-2 sm:grid-cols-2">
                  {[false, true].map((v) => (
                    <button
                      key={String(v)}
                      type="button"
                      onClick={() => patch({ alreadySown: v })}
                      className={cn(
                        'rounded-lg border p-3 text-left',
                        draft.alreadySown === v ? 'border-primary bg-primary/10' : 'border-border',
                      )}
                    >
                      <span className="block text-sm font-medium">
                        {v ? 'Посеян — сев уже прошёл' : 'Запланирован — сеять собираемся'}
                      </span>
                      <span className="mt-1 block text-xs text-muted-foreground">
                        {v ? 'создаст факт-операцию «Сев»' : 'операций не создаётся'}
                      </span>
                    </button>
                  ))}
                </div>
              )}

              {draft.alreadySown && !draft.fallowKind && (
                <div className="space-y-3 rounded-lg border border-border p-3">
                  <div className="flex flex-wrap items-center gap-2">
                    <Badge>будет создана тех.операция</Badge>
                    <span className="text-sm font-medium">
                      Сев · {draft.cropName ?? 'культура'} · {fieldName}
                    </span>
                  </div>
                  <div className="space-y-2">
                    <Label className="text-xs uppercase tracking-wide text-muted-foreground">
                      Тип тех.операции из справочника хозяйства
                    </Label>
                    <Select
                      value={effectiveOpTypeId ?? ''}
                      onValueChange={(v) => patch({ sowOperationTypeId: v })}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Выберите тип операции…" />
                      </SelectTrigger>
                      <SelectContent>
                        {(opTypes.data ?? []).map((t) => (
                          <SelectItem key={t.id} value={t.id}>
                            {t.local_name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <dl className="grid grid-cols-2 gap-2 text-sm sm:grid-cols-3">
                    <Cell label="Дата" value={draft.sowingDate ?? '—'} />
                    <Cell label="Площадь" value={`${draft.areaHa ?? '—'} га`} />
                    <Cell label="Густота" value={draft.seedingRate ?? '—'} />
                    <Cell label="Норма высева" value={draft.seedingNormKgHa ?? '—'} />
                    <Cell label="Глубина" value={draft.seedingDepthCm ?? '—'} />
                    <Cell label="Семян всего" value={seeds != null ? `${seeds} кг` : '—'} />
                  </dl>
                  <p className="text-xs text-muted-foreground">
                    Операция создастся выполненной, без агроработ: кто и на чём сеял —
                    дозаполняется позже на вкладке «Операции». Техника и люди не угадываются.
                  </p>
                </div>
              )}

              {leafBusy && (
                <label className="flex items-start gap-2 rounded-lg border border-border p-3 text-sm">
                  <input
                    type="checkbox"
                    className="mt-1"
                    checked={draft.isSecondCrop}
                    onChange={(e) => patch({ isSecondCrop: e.target.checked })}
                  />
                  <span>
                    <span className="block font-medium">
                      Это пожнивной посев — вторая культура после уборки первой
                    </span>
                    <span className="block text-xs text-muted-foreground">
                      Поле в этом сезоне уже занято. Без этой отметки второй посев не
                      сохранится — так повторное нажатие «Сохранить» не заведёт дубль.
                    </span>
                  </span>
                </label>
              )}

              <Alert>
                <AlertDescription className="text-xs">{outcomeSummary(draft)}</AlertDescription>
              </Alert>

              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-wide text-muted-foreground">
                  Комментарий к посеву · необязательно
                </Label>
                <Input
                  value={draft.comment}
                  onChange={(e) => patch({ comment: e.target.value })}
                  placeholder="напр.: сеяли в два захода, 26 и 28 апреля"
                />
              </div>
            </div>
          )}
        </div>

        {/* Красный — только на ответ сервера. Подсказка «что заполнить» приходит
            ДО действия и ругаться не должна: агроном ещё ничего не сделал. */}
        {error ? (
          <Alert variant="destructive">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        ) : blocker ? (
          <p className="text-xs text-muted-foreground">{blocker}</p>
        ) : null}

        <div className="flex flex-wrap items-center justify-between gap-2 border-t border-border pt-3">
          <Button
            variant="ghost"
            onClick={() =>
              stepIdx === 0 ? onOpenChange(false) : setStep(SOW_STEPS[stepIdx - 1])
            }
            disabled={sow.isPending}
          >
            {stepIdx === 0 ? 'Отмена' : 'Назад'}
          </Button>
          {step === 'status' ? (
            <div className="flex flex-wrap gap-2">
              <Button
                variant="outline"
                onClick={() => submit(true)}
                disabled={Boolean(blocker) || sow.isPending}
              >
                Сохранить и добавить ещё посев
              </Button>
              <Button onClick={() => submit()} disabled={Boolean(blocker) || sow.isPending}>
                {sow.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                Сохранить
              </Button>
            </div>
          ) : (
            <Button
              onClick={() => setStep(SOW_STEPS[stepIdx + 1])}
              disabled={Boolean(blocker)}
            >
              Дальше — {STEP_TITLES[SOW_STEPS[stepIdx + 1]].title.toLowerCase()} →
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function Cell({ label, value }: { label: string; value: string | number }) {
  return (
    <div className="rounded-md border border-border px-3 py-2">
      <dt className="text-xs uppercase tracking-wide text-muted-foreground">{label}</dt>
      <dd className="text-sm font-medium tabular-nums">{value}</dd>
    </div>
  );
}
